/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import Loadable from 'react-loadable';

import LoadingSpinner from 'components/LoadingSpinner';
import BlockedEcosystem from 'components/BlockedEcosystem';
import ExpiryWarning from 'components/ExpiryWarning';
import Footer from 'components/Footer';
import Header from 'components/Header';
import Sidebar from 'components/Sidebar';
import WedgeModal from 'components/WedgeModal';

import { ECOSYSTEM_STATUS, CONSTANTS, ROUTES } from 'enum';
import { resetRequestID } from 'store/ecosystems/actions';
import CommitFinishModal from 'Modals/CommitFinishModal';
import { loadEcosystem, refreshEcosystemStatus } from './scenario-actions';
import LoadableFallback from './LoadableFallback';

const BlockedStatus = [
  ECOSYSTEM_STATUS.COMMIT_IN_PROGRESS,
  ECOSYSTEM_STATUS.COMMIT_ERROR,
  ECOSYSTEM_STATUS.DISABLED,
  ECOSYSTEM_STATUS.ECOSYSTEM_ERROR,
  CONSTANTS.INACCESSIBLE,
];

const LoadableReports = Loadable({
  loader: () => import('views/Reports'),
  loading: LoadableFallback,
});

const LoadableTraffic = Loadable({
  loader: () => import('views/Reports/Traffic'),
  loading: LoadableFallback,
});

const LoadableGatewayReports = Loadable({
  loader: () => import('views/Reports/Gateways'),
  loading: LoadableFallback,
});

const LoadableUsersAndThings = Loadable({
  loader: () => import('views/Reports/UsersAndThings'),
  loading: LoadableFallback,
});

const LoadableVPNConnections = Loadable({
  loader: () => import('views/Reports/VPNConnections'),
  loading: LoadableFallback,
});

const LoadableLogSettings = Loadable({
  loader: () => import('views/Reports/LogSetting'),
  loading: LoadableFallback,
});

const LoadableGuidedTasks = Loadable({
  loader: () => import('views/GuidedTasks'),
  loading: LoadableFallback,
});

const LoadableObjects = Loadable({
  loader: () => import('views/Objects'),
  loading: LoadableFallback,
});

const LoadableThings = Loadable({
  loader: () => import('views/Objects/Things'),
  loading: LoadableFallback,
});

const LoadableGateways = Loadable({
  loader: () => import('views/Objects/Gateways'),
  loading: LoadableFallback,
});

const LoadableAddresses = Loadable({
  loader: () => import('views/Objects/Addresses'),
  loading: LoadableFallback,
});

const LoadableAllocatedIPs = Loadable({
  loader: () => import('views/Objects/AllocatedIPs'),
  loading: LoadableFallback,
});

const LoadableGroups = Loadable({
  loader: () => import('views/Objects/Groups'),
  loading: LoadableFallback,
});

const LoadableMFA = Loadable({
  loader: () => import('views/Objects/MFA'),
  loading: LoadableFallback,
});

const LoadableLDAPServer = Loadable({
  loader: () => import('views/Objects/Servers'),
  loading: LoadableFallback,
});

const LoadableLDAPConfig = Loadable({
  loader: () => import('views/Objects/Servers/LDAPConfig'),
  loading: LoadableFallback,
});

const LoadableEcoUsers = Loadable({
  loader: () => import('views/Objects/Users'),
  loading: LoadableFallback,
});

const LoadableEcoUserInvitation = Loadable({
  loader: () => import('views/Objects/Users/UserInvitation'),
  loading: LoadableFallback,
});

const LoadableSecurity = Loadable({
  loader: () => import('views/Security'),
  loading: LoadableFallback,
});

const LoadableSecurityProfile = Loadable({
  loader: () => import('views/SecurityProfile'),
  loading: LoadableFallback,
});

const LoadableDecryptionProfile = Loadable({
  loader: () => import('views/DecryptionProfiles'),
  loading: LoadableFallback,
});

const LoadableAddressTranslation = Loadable({
  loader: () => import('views/AddressTranslation'),
  loading: LoadableFallback,
});

const LoadableAssetManagement = Loadable({
  loader: () => import('views/AssetManagement'),
  loading: LoadableFallback,
});

const LoadableProfile = Loadable({
  loader: () => import('views/Profiles'),
  loading: LoadableFallback,
});

const LoadableAdminAccess = Loadable({
  loader: () => import('views/Settings/AdminAccess'),
  loading: LoadableFallback,
});

function Dashboard() {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const location = useLocation();
  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();
  const ecosystem = useSelector((state) => state.ecosystems.currentEcosystem);

  const [isInitializing, setIsInitializing] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);
  const [action, setAction] = useState('');

  const previousPathnameRef = useRef(location.pathname);
  const previousStatusRef = useRef();

  const closeMobileSidebar = useCallback(() => {
    document.documentElement.classList.remove('nav-open');
    const ele = document.getElementById('bodyClick');

    if (ele) {
      ele.parentElement?.removeChild(ele);
    }

    const wrapper = document.getElementById('root')?.getElementsByClassName('wrapper')[0];
    if (wrapper) {
      wrapper.style.overflow = 'auto';
    }

    const navbarElement = document.getElementsByClassName('navbar-collapse collapse in');
    if (navbarElement.length) {
      navbarElement[0].classList.remove('in');
    }
  }, []);

  const closeCommitModal = useCallback(() => {
    dispatch(resetRequestID({ ecoId: ecosystemUUID }));
    setOpenSuccess(false);
    setOpenFailed(false);
  }, [dispatch, ecosystemUUID]);

  useEffect(() => {
    let active = true;
    let intervalId;

    setIsInitializing(true);
    setLoaded(false);

    const splittedPathname = history.location.pathname.split('/');
    const ecosystemURL = splittedPathname.slice(0, 6).join('/');
    const customersURL = splittedPathname.slice(0, 5).join('/');

    if (splittedPathname.length === 6 || (splittedPathname.length === 7 && splittedPathname[6] === '')) {
      history.replace(`${ecosystemURL}/objects`);
    }

    const initializeDashboard = async () => {
      try {
        await dispatch(loadEcosystem({ customerUUID, ecosystemUUID }));

        const {
          ecosystems: { currentEcosystem },
        } = store.getState();
        if (!active) {
          return;
        }

        if (
          currentEcosystem &&
          ![
            ECOSYSTEM_STATUS.COMMIT_ERROR,
            ECOSYSTEM_STATUS.DISABLED,
            ECOSYSTEM_STATUS.ECOSYSTEM_ERROR,
            CONSTANTS.INACCESSIBLE,
          ].includes(currentEcosystem.status)
        ) {
          await dispatch(refreshEcosystemStatus({ customerUUID, ecosystemUUID }));
          if (!active) {
            return;
          }

          intervalId = window.setInterval(() => {
            dispatch(refreshEcosystemStatus({ customerUUID, ecosystemUUID })).catch(() => {});
          }, 5000);

          setLoaded(true);
          setIsInitializing(false);
          return;
        }

        setIsInitializing(false);
        history.replace(customersURL);
      } catch (err) {
        if (!active) {
          return;
        }

        setLoaded(false);
        setIsInitializing(false);

        if (err?.response?.status !== 401) {
          history.replace(customersURL);
        }
      }
    };

    initializeDashboard();

    return () => {
      active = false;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [customerUUID, dispatch, ecosystemUUID, history, store]);

  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      if (window.innerWidth < 993 && document.documentElement.classList.contains('nav-open')) {
        closeMobileSidebar();
      }

      if (history.action === 'PUSH') {
        if (document.documentElement) {
          document.documentElement.scrollTop = 0;
        }
        if (document.scrollingElement) {
          document.scrollingElement.scrollTop = 0;
        }
      }
    }

    previousPathnameRef.current = location.pathname;
  }, [closeMobileSidebar, history.action, location.pathname]);

  useEffect(() => () => closeMobileSidebar(), [closeMobileSidebar]);

  useEffect(() => {
    if (!ecosystem) {
      previousStatusRef.current = undefined;
      return;
    }

    const { status, commitID, revertID } = ecosystem;
    const previousStatus = previousStatusRef.current;

    if (status !== ECOSYSTEM_STATUS.PENDING_CHANGES && status !== previousStatus && (commitID || revertID)) {
      setAction(commitID ? 'Commit' : 'Revert');
      if (status === ECOSYSTEM_STATUS.OK) {
        setOpenSuccess(true);
      } else if (status === ECOSYSTEM_STATUS.COMMIT_ERROR) {
        setOpenFailed(true);
      }
    }

    previousStatusRef.current = status;
  }, [ecosystem]);

  const renderMainPanel = () => {
    const blocked = BlockedStatus.includes(ecosystem?.status);

    if (isInitializing) {
      return <LoadingSpinner />;
    }
    if (blocked) {
      return <BlockedEcosystem status={ecosystem.status} />;
    }
    if (loaded) {
      return (
        <Switch>
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/guided-tasks`} component={LoadableGuidedTasks} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/things/:id`} component={LoadableThings} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/things`} component={LoadableThings} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/gateways`} component={LoadableGateways} />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/addresses`}
            component={LoadableAddresses}
          />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/publicAddresses`}
            component={LoadableAllocatedIPs}
          />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/groups`} component={LoadableGroups} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/mfa`} component={LoadableMFA} />
          <Route
            exact
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/servers/new`}
            component={LoadableLDAPConfig}
          />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/servers/:id`}
            component={LoadableLDAPConfig}
          />
          <Route
            exact
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/servers`}
            component={LoadableLDAPServer}
          />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/users/invitations`}
            component={LoadableEcoUserInvitation}
          />
          <Route
            exact
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects/users`}
            component={LoadableEcoUsers}
          />
          <Route exact path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/objects`} component={LoadableObjects} />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/security/web-filter`}
            component={LoadableSecurityProfile}
          />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/security/decryption`}
            component={LoadableDecryptionProfile}
          />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/security`} component={LoadableSecurity} />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/addresstranslations`}
            component={LoadableAddressTranslation}
          />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/assets`} component={LoadableAssetManagement} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/logs/traffic`} component={LoadableTraffic} />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/logs/gateway`}
            component={LoadableGatewayReports}
          />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/logs/usersandthings`}
            component={LoadableUsersAndThings}
          />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/logs/vpn-connections`}
            component={LoadableVPNConnections}
          />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/logs/settings`} component={LoadableLogSettings} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/logs`} component={LoadableReports} />
          <Route path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/profile`} component={LoadableProfile} />
          <Route
            path={`${ROUTES.CUSTOMERS}/:cusID/ecosystems/:ecoID/settings/admin-access`}
            component={LoadableAdminAccess}
          />
          <Redirect to={ROUTES.NOT_FOUND} />
        </Switch>
      );
    }
    return null;
  };

  const { commitID, revertID } = ecosystem || {};

  return (
    <div className="wrapper">
      <ExpiryWarning />
      <Sidebar />
      <div id="main-panel" className="main-panel">
        <Header location={location} dashboardHeader />
        {renderMainPanel()}
        <Footer />
      </div>
      <WedgeModal
        isOpen={openSuccess || openFailed}
        onClose={closeCommitModal}
        title={openSuccess ? `${action} Success!` : `${action} Refused!`}
        size="small"
      >
        <CommitFinishModal
          ecosystem={ecosystem}
          success={openSuccess}
          onClose={closeCommitModal}
          requestID={action === 'Commit' ? commitID : revertID}
          action={action}
        />
      </WedgeModal>
    </div>
  );
}

export default Dashboard;
